import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import {
  DrugSubstanceDose,
  DoseAlternative,
} from '../../types/DrugSubstanceDose.type';
import { useDrugSubstanceDoses } from '../../contexts/api-contexts/DrugSubstanceDosesContext';
import { DrugDosageAlternativesTable } from './components/DrugDosageAlternativesTable';
import { CalculationTypeSelector } from './components/CalculationTypeSelector';
import { SingleCalculatorForm } from './components/SingleCalculatorForm';
import {
  CalculationType,
  CalculationUnit,
  CalculatedValues,
} from '../../types/DrugDosageCalculator.type';

export interface DrugDosageCalculatorWithInfoProps {
  /** Drug substance ID */
  drugSubstanceId: number;
  /** Drug substance name (if known, otherwise will be fetched) */
  drugSubstanceName?: string;
  /** Animal type name for display */
  animalTypeName?: string;
  /** Disease name for context */
  diseaseName?: string;
  /** Initial patient weight in kg */
  patientWeightKg?: number;
  /** Whether weight field is disabled */
  weightDisabled?: boolean;
  /**
   * Optional: Treatment ID to filter doses.
   * When provided, the API will return only doses linked to this treatment.
   * Use this when opening the calculator from a specific treatment context.
   */
  treatmentId?: number;
  /** Callback when calculation is performed */
  onCalculation?: (
    calculationType: CalculationType,
    values: CalculatedValues<CalculationUnit>,
    selectedAlternative?: DoseAlternative,
    selectedDose?: DrugSubstanceDose
  ) => void;
}

/**
 * Drug Dosage Calculator with full dosage information.
 * Shows drug substance info, dosage alternatives, and calculator.
 */
export const DrugDosageCalculatorWithInfo: React.FC<
  DrugDosageCalculatorWithInfoProps
> = ({
  drugSubstanceId,
  drugSubstanceName,
  animalTypeName,
  diseaseName,
  patientWeightKg,
  weightDisabled = false,
  treatmentId,
  onCalculation,
}) => {
  const {
    fetchDrugSubstanceDoses,
    fetchDrugSubstanceInfo,
    getDrugSubstanceDoses,
    getDrugSubstanceInfo,
    isLoadingDoses,
    isLoadingInfo,
    error,
  } = useDrugSubstanceDoses();

  const [selectedAlternative, setSelectedAlternative] =
    useState<DoseAlternative | null>(null);
  const [selectedDose, setSelectedDose] = useState<DrugSubstanceDose | null>(
    null
  );
  const [selectedCalculationType, setSelectedCalculationType] =
    useState<CalculationType | null>(null);

  // Ref for scrolling calculator into view
  const calculatorRef = useRef<HTMLDivElement>(null);
  const [showCalculator, setShowCalculator] = useState(false);

  // Fetch data on mount - pass treatmentId for API-side filtering
  useEffect(() => {
    fetchDrugSubstanceDoses(drugSubstanceId, treatmentId);
    fetchDrugSubstanceInfo(drugSubstanceId);
  }, [
    drugSubstanceId,
    treatmentId,
    fetchDrugSubstanceDoses,
    fetchDrugSubstanceInfo,
  ]);

  // Get doses - API already filtered by treatmentId if provided
  const doses = getDrugSubstanceDoses(drugSubstanceId, treatmentId);
  const substanceInfo = getDrugSubstanceInfo(drugSubstanceId);
  const loading =
    isLoadingDoses(drugSubstanceId, treatmentId) ||
    isLoadingInfo(drugSubstanceId);

  const displayName =
    drugSubstanceName || substanceInfo?.name || 'Drug Substance';

  // Scroll calculator into view when it's shown
  useEffect(() => {
    if (showCalculator && calculatorRef.current) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        calculatorRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [showCalculator, selectedCalculationType]);

  // Handle alternative selection
  const handleSelectAlternative = (
    alternative: DoseAlternative,
    dose: DrugSubstanceDose
  ) => {
    setSelectedAlternative(alternative);
    setSelectedDose(dose);
    setSelectedCalculationType(null); // Reset to show type selector
    setShowCalculator(true);
  };

  // Handle calculation type selection
  const handleSelectCalculationType = (type: CalculationType) => {
    setSelectedCalculationType(type);
  };

  // Handle back to type selection
  const handleBackToTypeSelection = () => {
    setSelectedCalculationType(null);
  };

  // Handle calculation
  const handleCalculation = (
    calculationType: CalculationType,
    values: CalculatedValues<CalculationUnit>
  ) => {
    onCalculation?.(
      calculationType,
      values,
      selectedAlternative || undefined,
      selectedDose || undefined
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with context */}
      <Box sx={{ mb: 3 }}>
        {diseaseName && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 'bold',
              letterSpacing: 1.5,
              color: 'text.secondary',
              textTransform: 'uppercase',
            }}
          >
            {diseaseName}
          </Typography>
        )}
        <Typography
          variant="caption"
          component="div"
          sx={{
            fontWeight: 'bold',
            letterSpacing: 0.5,
            color: 'text.secondary',
            textTransform: 'uppercase',
          }}
        >
          {displayName} DOSAGES{animalTypeName ? ` FOR ${animalTypeName}` : ''}
        </Typography>
      </Box>

      {/* Drug substance info */}
      {substanceInfo?.info && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {substanceInfo.info}
          </Typography>
        </Paper>
      )}

      {/* Dosage alternatives */}
      {doses && doses.length > 0 ? (
        doses.map((dose) => (
          <DrugDosageAlternativesTable
            key={dose.id}
            dose={dose}
            drugSubstanceName={displayName}
            onSelectAlternative={handleSelectAlternative}
          />
        ))
      ) : (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No dosage information available for this drug substance.
          </Typography>
        </Paper>
      )}

      {/* Calculator section - shown after selecting an alternative */}
      {showCalculator && selectedAlternative && selectedDose && (
        <Paper ref={calculatorRef} variant="outlined" sx={{ p: 3, mt: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                letterSpacing: 1.5,
                color: 'text.secondary',
              }}
            >
              SELECTED: {selectedDose.drug_indication.name}
            </Typography>
            {selectedAlternative.phases[0] && (
              <Typography variant="body2" color="text.secondary">
                {selectedAlternative.phases[0].dose_type.name} -{' '}
                {selectedAlternative.phases[0].drug_administration_route.name}
              </Typography>
            )}
          </Box>

          {/* Step 1: Show calculation type selector */}
          {!selectedCalculationType && (
            <CalculationTypeSelector onSelect={handleSelectCalculationType} />
          )}

          {/* Step 2: Show the specific calculator form */}
          {selectedCalculationType && (
            <SingleCalculatorForm
              calculationType={selectedCalculationType}
              initialWeightKg={patientWeightKg?.toString() || ''}
              weightDisabled={weightDisabled}
              onCalculation={handleCalculation}
              onBack={handleBackToTypeSelection}
            />
          )}
        </Paper>
      )}
    </Box>
  );
};

export default DrugDosageCalculatorWithInfo;
